import { Controller } from 'react-hook-form';
import { Select, SelectSize, Textarea } from '@ui/src';
import InfoRow from '../../common/info-row';
import StepPartBlock from '../../common/step-part-block';
import { AdditionalInformationBlockTypes } from './types';
import { NewApplicationStatus } from '../../../../hoc/common/types';

import commonStyles from '../../common/styles.module.css';

const Checklist = ({
  applicationScheme,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  status,
  isEditable,
  questionnarieFields,
}: AdditionalInformationBlockTypes) => (
  <StepPartBlock title="Checklist of questions">
    <>
      {applicationScheme?.transactionMonitoringActive && (
        <InfoRow
          title="Do you carry out transaction monitoring?"
          id="hasTransactionMonitoring"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="hasTransactionMonitoring"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="hasTransactionMonitoring"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['hasTransactionMonitoring']);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={questionnarieErrors?.hasTransactionMonitoring?.message}
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.hasTransactionMonitoring === 'yes' && (
              <InfoRow
                title="Is your transaction monitoring"
                id="transactionMonitoringKind"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="transactionMonitoringKind"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="transactionMonitoringKind"
                      placeholder="Select option"
                      list={[
                        {
                          name: 'Automated',
                          value: 'AUTO',
                        },
                        {
                          name: 'Manual',
                          value: 'MANUAL',
                        },
                      ]}
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['transactionMonitoringKind']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={
                        questionnarieErrors?.transactionMonitoringKind?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
      {applicationScheme?.screeningActive && (
        <InfoRow
          title="Do you carry out sanction screening, adverse media screening, and PEP screening?"
          id="hasScreening"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="hasScreening"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="hasScreening"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['hasScreening']);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={questionnarieErrors?.hasScreening?.message}
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.hasScreening === 'yes' && (
              <InfoRow
                title="Is your screening"
                id="screeningKind"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="screeningKind"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="screeningKind"
                      placeholder="Select option"
                      list={[
                        {
                          name: 'Automated',
                          value: 'AUTO',
                        },
                        {
                          name: 'Manual',
                          value: 'MANUAL',
                        },
                      ]}
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['screeningKind']);
                      }}
                      className="borderless"
                      size={SelectSize.SMALL}
                      error={questionnarieErrors?.screeningKind?.message}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      fullWidth
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
      {applicationScheme?.regulatoryInvestigationActive && (
        <InfoRow
          title="Have you been under any regulatory investigation in the last 24 months?"
          id="hasRegulatoryInvestigation"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="hasRegulatoryInvestigation"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="hasRegulatoryInvestigation"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['hasRegulatoryInvestigation']);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={
                    questionnarieErrors?.hasRegulatoryInvestigation?.message
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.hasRegulatoryInvestigation === 'yes' && (
              <InfoRow
                title="Provide details"
                id="regulatoryInvestigationDetails"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="regulatoryInvestigationDetails"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="regulatoryInvestigationDetails"
                      placeholder="Enter details..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'regulatoryInvestigationDetails',
                        ]);
                      }}
                      className={commonStyles.provideDetailsTextarea}
                      error={
                        questionnarieErrors?.regulatoryInvestigationDetails
                          ?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
      {applicationScheme?.visitedByAuthoritiesActive && (
        <InfoRow
          title="Have you been visited by any of the following authorities in the last 24 months?"
          id="visitedByAuthorities"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="visitedByAuthorities"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="visitedByAuthorities"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'HMRC',
                      value: 'HMRC',
                    },
                    {
                      name: 'FCA',
                      value: 'FCA',
                    },
                    {
                      name: 'NCA',
                      value: 'NCA',
                    },
                    {
                      name: 'Metropolitan Police',
                      value: 'METROPOLITAN_POLICE',
                    },
                    {
                      name: 'No',
                      value: 'NO',
                    },
                  ]}
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['visitedByAuthorities']);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={
                    questionnarieErrors?.hasRegulatoryInvestigation?.message
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.visitedByAuthorities !== 'NO' && (
              <InfoRow
                title="Provide details"
                id="visitedByAuthoritiesDetails"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="visitedByAuthoritiesDetails"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="visitedByAuthoritiesDetails"
                      placeholder="Enter details..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'visitedByAuthoritiesDetails',
                        ]);
                      }}
                      className={commonStyles.provideDetailsTextarea}
                      error={
                        questionnarieErrors?.visitedByAuthoritiesDetails
                          ?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
      {applicationScheme?.onboardCorporateClientsActive && (
        <InfoRow
          title="Do you onboard corporate clients?"
          id="hasOnboardCorporateClients"
          lessPadding
        >
          <Controller
            control={questionnarieControl}
            name="hasOnboardCorporateClients"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                name="hasOnboardCorporateClients"
                placeholder="Select «Yes» or «No»"
                list={[
                  {
                    name: 'Yes',
                    value: 'yes',
                  },
                  {
                    name: 'No',
                    value: 'no',
                  },
                ]}
                value={value as string}
                onChange={(e) => {
                  onChange(e);
                  questionnarieClearErrors(['hasOnboardCorporateClients']);
                }}
                className="borderless"
                size={SelectSize.SMALL}
                error={questionnarieErrors?.hasOnboardCorporateClients?.message}
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                fullWidth
              />
            )}
          />
        </InfoRow>
      )}
      {applicationScheme?.onboardMsbActive && (
        <InfoRow
          title="Do you onboard Money Service Businesses (MSBs)?"
          id="hasOnboardMsb"
          lessPadding
        >
          <Controller
            control={questionnarieControl}
            name="hasOnboardMsb"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                name="hasOnboardMsb"
                placeholder="Select «Yes» or «No»"
                list={[
                  {
                    name: 'Yes',
                    value: 'yes',
                  },
                  {
                    name: 'No',
                    value: 'no',
                  },
                ]}
                value={value as string}
                onChange={(e) => {
                  onChange(e);
                  questionnarieClearErrors(['hasOnboardMsb']);
                }}
                className="borderless"
                size={SelectSize.SMALL}
                error={questionnarieErrors?.hasOnboardMsb?.message}
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                fullWidth
              />
            )}
          />
        </InfoRow>
      )}
      {applicationScheme?.paymentsForCharitiesActive && (
        <InfoRow
          title="Do you make payments for charities?"
          id="hasPaymentsForCharities"
          lessPadding
        >
          <Controller
            control={questionnarieControl}
            name="hasPaymentsForCharities"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Select
                name="hasPaymentsForCharities"
                placeholder="Select «Yes» or «No»"
                list={[
                  {
                    name: 'Yes',
                    value: 'yes',
                  },
                  {
                    name: 'No',
                    value: 'no',
                  },
                ]}
                value={value as string}
                onChange={(e) => {
                  onChange(e);
                  questionnarieClearErrors(['hasPaymentsForCharities']);
                }}
                className="borderless"
                size={SelectSize.SMALL}
                error={questionnarieErrors?.hasPaymentsForCharities?.message}
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                fullWidth
              />
            )}
          />
        </InfoRow>
      )}
      {applicationScheme?.changesInManagementOrOwnershipActive && (
        <InfoRow
          title="Is the company undergoing any changes in management or ownership?"
          id="hasChangesInManagementOrOwnership"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="hasChangesInManagementOrOwnership"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="hasChangesInManagementOrOwnership"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors([
                      'hasChangesInManagementOrOwnership',
                    ]);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={
                    questionnarieErrors?.hasChangesInManagementOrOwnership
                      ?.message
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.hasChangesInManagementOrOwnership ===
              'yes' && (
              <InfoRow
                title="Provide details"
                id="changesInManagementOrOwnershipDetails"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="changesInManagementOrOwnershipDetails"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="changesInManagementOrOwnershipDetails"
                      placeholder="Enter details..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'changesInManagementOrOwnershipDetails',
                        ]);
                      }}
                      className={commonStyles.provideDetailsTextarea}
                      error={
                        questionnarieErrors
                          ?.changesInManagementOrOwnershipDetails?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
      {applicationScheme?.workingWithAnyOtherIPSPActive && (
        <InfoRow
          title="Are you currently working with any other IPSP (Intermediary Payment Service Provider)?"
          id="hasWorkingWithAnyOtherIpsp"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="hasWorkingWithAnyOtherIpsp"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="hasWorkingWithAnyOtherIpsp"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors(['hasWorkingWithAnyOtherIpsp']);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={
                    questionnarieErrors?.hasWorkingWithAnyOtherIpsp?.message
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.hasWorkingWithAnyOtherIpsp === 'yes' && (
              <InfoRow
                title="Provide details"
                id="workingWithAnyOtherIpspDetails"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="workingWithAnyOtherIpspDetails"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="workingWithAnyOtherIpspDetails"
                      placeholder="Enter details..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'workingWithAnyOtherIpspDetails',
                        ]);
                      }}
                      className={commonStyles.provideDetailsTextarea}
                      error={
                        questionnarieErrors?.workingWithAnyOtherIpspDetails
                          ?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
      {applicationScheme?.auditedByExternalComplianceActive && (
        <InfoRow
          title="Has the institution been audited by external compliance auditors in the past 12 months?"
          id="hasAuditedByExternalCompliance"
          lessPadding
        >
          <>
            <Controller
              control={questionnarieControl}
              name="hasAuditedByExternalCompliance"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="hasAuditedByExternalCompliance"
                  placeholder="Select «Yes» or «No»"
                  list={[
                    {
                      name: 'Yes',
                      value: 'yes',
                    },
                    {
                      name: 'No',
                      value: 'no',
                    },
                  ]}
                  value={value as string}
                  onChange={(e) => {
                    onChange(e);
                    questionnarieClearErrors([
                      'hasAuditedByExternalCompliance',
                    ]);
                  }}
                  className="borderless"
                  size={SelectSize.SMALL}
                  error={
                    questionnarieErrors?.hasAuditedByExternalCompliance?.message
                  }
                  tabIndex={
                    !isEditable &&
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
            {questionnarieFields?.hasAuditedByExternalCompliance === 'yes' && (
              <InfoRow
                title="Provide details"
                id="auditedByExternalComplianceDetails"
                className={commonStyles.withoutPaddingInfoRow}
              >
                <Controller
                  control={questionnarieControl}
                  name="auditedByExternalComplianceDetails"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <Textarea
                      name="auditedByExternalComplianceDetails"
                      placeholder="Enter details..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors([
                          'auditedByExternalComplianceDetails',
                        ]);
                      }}
                      className={commonStyles.provideDetailsTextarea}
                      error={
                        questionnarieErrors?.auditedByExternalComplianceDetails
                          ?.message
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        </InfoRow>
      )}
    </>
  </StepPartBlock>
);

export default Checklist;
